import { Button } from "components/DesignSystem/Button/Button";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { FormikForm } from "components/FormikForm/FormikForm";
import { Info } from "components/icons/Info";
import { PriceInput } from "components/PriceInput/PriceInput";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { getIn, Field, FieldProps } from "formik";
import { AccountMigrateMap } from "store/apis/bankConnections";

type ReviewAccountsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  unmappedAccounts: (AccountMigrateMap & { balance: string })[];
  onConfirm: (accountActions: AccountMigrateMap[]) => void;
};

export const ReviewAccountsModal = ({
  isOpen,
  onClose,
  unmappedAccounts,
  onConfirm,
}: ReviewAccountsModalProps) => {
  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="large" asChild onClick={(e) => e.stopPropagation()}>
        <FormikForm
          initialValues={{
            accounts: unmappedAccounts.map((account) => ({
              ...account,
              closed_on: account.closed_on || dayjs().format(YYYY_MM_DD),
            })),
          }}
          onSubmit={(values) => onConfirm(values.accounts)}
        >
          {({ values, setFieldValue, submitForm }) => {
            return (
              <>
                <Modal.Header className="sm:t-pl-4">
                  <Modal.Title>Review accounts</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-flex t-flex-col t-gap-6 sm:t-px-4">
                  <p className="t-text-body-sm t-m-0 t-text-text-60">
                    These accounts were previously added/connected but are not
                    part of the new integration. Please choose how you'd like to
                    proceed for each:
                  </p>

                  {unmappedAccounts.map((account, index) => (
                    <div
                      key={account.account_id}
                      className="t-border t-rounded-lg t-border-solid t-border-neutral-10 t-bg-white t-p-4 t-flex t-flex-col t-gap-4"
                    >
                      <div className="t-flex t-flex-col t-gap-0.5">
                        <p className="t-m-0 t-text-subtext t-flex t-items-center t-gap-2">
                          <span>
                            {account.name} •••• {account.mask}
                          </span>
                          <span className="t-inline-block t-w-2.5 t-h-2.5 t-bg-red t-border-solid t-border-red-10 t-border-2 t-rounded-full"></span>
                        </p>
                        <p className="t-text-body-sm t-m-0 t-text-text-30">
                          Account Balance: ${account.balance || 0}
                        </p>
                      </div>

                      <div className="t-border-0 t-border-dashed t-border-neutral-10 t-border-b" />

                      <RadioGroup.Root
                        value={getIn(values, `accounts.${index}.action`)}
                        onValueChange={(value) => {
                          const newAccounts = values.accounts.slice();
                          newAccounts[index].action = value as
                            | "closed"
                            | "keep_active";

                          if (value === "keep_active") {
                            newAccounts[index].closed_on = null as any;
                            newAccounts[index].closing_balance = undefined;
                          } else if (value === "closed") {
                            newAccounts[index].closed_on =
                              dayjs().format(YYYY_MM_DD);
                          }

                          setFieldValue("accounts", newAccounts);
                        }}
                      >
                        <RadioGroup.Content className="t-flex !t-flex-row t-gap-5 t-text-text-60">
                          <RadioGroup.Item
                            id={`keep_active_${index}`}
                            value="keep_active"
                            className="t-flex-1"
                          >
                            <span className="t-text-body-sm t-whitespace-nowrap">
                              Keep account active (Manual uploads only)
                            </span>
                          </RadioGroup.Item>
                          <RadioGroup.Item
                            id={`closed_${index}`}
                            value="closed"
                          >
                            <span className="t-text-body-sm">
                              Mark account as closed
                            </span>
                          </RadioGroup.Item>
                        </RadioGroup.Content>
                      </RadioGroup.Root>

                      {values.accounts[index]?.action === "closed" && (
                        <div className="t-mt-4 t-space-y-3">
                          <div>
                            <Field name={`accounts.${index}.closed_on`}>
                              {({ field }: FieldProps) => (
                                <DateInput
                                  {...field}
                                  portalId={field.name}
                                  label="Account closed on"
                                  saveFormat={YYYY_MM_DD}
                                  onBlur={() => {}}
                                />
                              )}
                            </Field>
                          </div>
                          <div className="t-grid t-grid-cols-2 t-gap-3 sm:t-grid-cols-1">
                            <PriceInput
                              label="Balance as of closing date"
                              required
                              name={`accounts.${index}.closing_balance`}
                              className="t-w-full t-px-3 t-py-2 t-border t-rounded"
                            />
                            <PriceInput
                              name={`accounts.${index}.balance`}
                              label="Inkle balance"
                              disabled
                            />
                          </div>
                          <div className="t-flex t-gap-1.5">
                            <span className="t-text-text-30 t-flex t-mt-0.5">
                              <Info
                                stroke="1.4"
                                color="currentColor"
                                size="12"
                              />
                            </span>
                            <p className="t-m-0 t-italic t-text-body-sm-italic t-text-text-60 sm:t-text-caption-italic">
                              Once confirmed, this account will be marked as
                              closed - no future transactions will be synced or
                              added manually.
                            </p>
                          </div>
                        </div>
                      )}

                      {values.accounts[index]?.action === "keep_active" && (
                        <div className="t-flex t-gap-1.5 t-items-start">
                          <span className="t-text-text-30 t-flex t-mt-0.5">
                            <Info stroke="1.4" color="currentColor" size="12" />
                          </span>

                          <p className="t-m-0 t-italic t-text-body-sm-italic t-text-text-60 sm:t-text-caption-italic">
                            This account will remain active - once confirmed, no
                            transactions will be fetched automatically, but you
                            can continue adding them manually.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button customType="primary" onClick={submitForm}>
                    Confirm
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
